import { of, Subject } from 'rxjs';
import { switchMap, takeWhile, mergeMap, buffer, filter } from 'rxjs/operators';
import { jobGuidManagerFactory } from '@shipstation/async-client-manager';

import { createPusherMessageCache } from '../pusher/message-cache';
import { BATCH_VALIDATED } from '../../services/pusher-service/pusher-contract-messages';

import { VALIDATION_ALLOWED_RESTARTS } from './constants';

const addMessageBuffer = (source$) => {
  const bufferFlush$ = new Subject();
  return source$.pipe(
    mergeMap((pusherMessage) => {
      if (pusherMessage.messageGuid) {
        return of(pusherMessage);
      }

      return of({
        messageGuid: pusherMessage.job,
        data: {
          ...pusherMessage,
        },
      });
    }),
    buffer(bufferFlush$),
    filter((messages) => messages.length),
  );
};

const completeCheck = (pusherMessage, jobGuids) => {
  const [jobGuid] = jobGuids;
  return !!(pusherMessage?.messageGuid === jobGuid && pusherMessage?.data?.final);
};

const resetTimeoutCheck = (pusherMessage, jobGuids) => {
  const [jobGuid] = jobGuids;
  return pusherMessage?.job === jobGuid;
};

const timeoutCompleteCheck = ({ messages }) => {
  return messages.find((msg) => msg?.data?.final);
};

const timeoutRetryCheck = ({ messages, retryCount }) => {
  return !timeoutCompleteCheck({ messages }) && retryCount < VALIDATION_ALLOWED_RESTARTS;
};

const timeoutFailureCheck = ({ messages, retryCount }) => {
  return !timeoutCompleteCheck({ messages }) && retryCount >= VALIDATION_ALLOWED_RESTARTS;
};

export const requestAndGetValidationMessages = ({
  curriedApi,
  isRetry,
  onRetryJob,
  onValidationTimeout,
  pusherTimeoutMs,
  logContext = {},
}) => {
  const logContextWithJobGuids = { ...logContext };
  const jobGuidManager = jobGuidManagerFactory();
  const fallbackOptions = {
    timeoutMs: pusherTimeoutMs,
    completeCheck,
    resetTimeoutCheck,
    timeoutCompleteCheck,
    timeoutRetryCheck,
    timeoutFailureCheck,
    onTimeout: () => {
      if (!isRetry) {
        onRetryJob();
        return;
      }
      onValidationTimeout(jobGuidManager.getJobGuids());
    },
    logContext: logContextWithJobGuids,
    jobGuidManager,
  };
  const { findMessage } = createPusherMessageCache(BATCH_VALIDATED, fallbackOptions);

  return curriedApi().pipe(
    switchMap(({ jobGuids }) => {
      jobGuidManager.addJobGuid(jobGuids[0]);
      logContextWithJobGuids.jobGuids = jobGuids;
      const messageChecker = (pusherMessage) => {
        const pusherGuid = pusherMessage.messageGuid || pusherMessage.job;
        return jobGuids.includes(pusherGuid);
      };

      const findMessage$ = findMessage(messageChecker).pipe(takeWhile((message) => !message?.data?.final, true));
      return of({
        jobGuids,
        findMessage$: addMessageBuffer(findMessage$),
      });
    }),
  );
};
